import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Car, Search, Fuel, Gauge, Wrench, FileText, User } from "lucide-react";
import { ModuleHeaderIcon } from "@/components/ModuleHeader";

const Vehiculos = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    fetchVehicles();
  }, []);

  const fetchVehicles = async () => {
    try {
      const res = await axios.get(`${API}/vehicles/assigned`, { withCredentials: true });
      setVehicles(res.data);
    } catch (err) {
      console.error("Error fetching vehicles:", err);
      toast.error("Error al cargar vehículos");
    } finally {
      setLoading(false);
    }
  };

  const filtered = vehicles.filter((v) => {
    const q = search.toLowerCase();
    return (
      !q ||
      v.descripcion?.toLowerCase().includes(q) ||
      v.marca?.toLowerCase().includes(q) ||
      v.modelo?.toLowerCase().includes(q) ||
      v.placas?.toLowerCase().includes(q) ||
      v.responsable_nombre?.toLowerCase().includes(q) ||
      v.serie_vin?.toLowerCase().includes(q)
    );
  });

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ModuleHeaderIcon icon={Car} />
          <div>
            <h1 className="text-2xl font-bold text-slate-800" data-testid="vehiculos-title">
              Vehículos
            </h1>
            <p className="text-slate-500 mt-1">
              Gestión de flotilla vehicular asignada
            </p>
          </div>
        </div>
        <Badge variant="secondary" className="text-sm px-3 py-1">
          {vehicles.length} vehículo{vehicles.length !== 1 ? "s" : ""} asignado{vehicles.length !== 1 ? "s" : ""}
        </Badge>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg"><Car className="w-5 h-5 text-blue-600" /></div>
              <div><p className="text-2xl font-bold">{vehicles.length}</p><p className="text-xs text-slate-500">Total</p></div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg"><Gauge className="w-5 h-5 text-green-600" /></div>
              <div><p className="text-2xl font-bold">{vehicles.filter(v => v.estatus === "propio").length}</p><p className="text-xs text-slate-500">Propios</p></div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg"><FileText className="w-5 h-5 text-amber-600" /></div>
              <div><p className="text-2xl font-bold">{vehicles.filter(v => v.estatus === "leasing").length}</p><p className="text-xs text-slate-500">Leasing</p></div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg"><User className="w-5 h-5 text-purple-600" /></div>
              <div><p className="text-2xl font-bold">{new Set(vehicles.map(v => v.responsable_id)).size}</p><p className="text-xs text-slate-500">Conductores</p></div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Buscar por descripción, marca, modelo, placas, conductor..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
          data-testid="vehicle-search-input"
        />
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-slate-400">
            <Car className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>{vehicles.length === 0 ? "No hay vehículos asignados. Asigna un activo fijo tipo VEH a un empleado para verlo aquí." : "Sin resultados para la búsqueda."}</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Clave</TableHead>
                  <TableHead>Descripción</TableHead>
                  <TableHead>Marca / Modelo</TableHead>
                  <TableHead>Placas</TableHead>
                  <TableHead>Asignado a</TableHead>
                  <TableHead>Estatus</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((v) => (
                  <TableRow
                    key={v.id}
                    className="cursor-pointer hover:bg-slate-50 transition-colors"
                    onClick={() => navigate(`/logistica/vehiculos/${v.id}`)}
                    data-testid={`vehicle-row-${v.id}`}
                  >
                    <TableCell className="font-mono text-sm">{v.clave}</TableCell>
                    <TableCell className="font-medium">{v.descripcion}</TableCell>
                    <TableCell className="text-sm">{v.marca} {v.modelo}</TableCell>
                    <TableCell className="font-mono font-medium">{v.placas || "—"}</TableCell>
                    <TableCell className="text-sm">{v.responsable_nombre || "—"}</TableCell>
                    <TableCell>
                      <Badge className={v.estatus === "propio" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}>
                        {v.estatus === "propio" ? "Propio" : "Leasing"}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}
    </div>
  );
};

export default Vehiculos;
